import { Button, Table } from '@alifd/next';
import { Box } from '@mui/material';
import { Select } from 'antd';
import axios from 'axios';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import PageHeader from '@/components/PageHeader';
import { RootState } from '@/redux/store';
import {
  ListItem,
  ResponseData,
} from '@/types/api/questionnaire/getQuestionnaireTitleFontDesk';

import QuestionnaireForm from './components/QuestionnaireForm';

export type Data = ListItem & {
  filler: string;
  year: string;
  month: string;
};

const CompanyRating: React.FC<{ showHeader: boolean }> = ({ showHeader }) => {
  const [dataSource, setDataSource] = useState<Data[]>([]);
  const [questionnaire, setQuestionnaire] = useState<{
    show: boolean;
    currentQuestionnaire: Data | null;
  }>({
    show: false,
    currentQuestionnaire: null,
  });
  const [trigger, setTrigger] = useState({});
  const [status, setStatus] = useState<string>();
  const [year, setYear] = useState<string>();
  const [month, setMonth] = useState<string>();
  const [yearOptions, setYearOptions] = useState<
    {
      label: string;
      value: string;
    }[]
  >([]);
  const [monthOptions, setMonthOptions] = useState<
    {
      label: string;
      value: string;
    }[]
  >([]);
  const user = useSelector((state: RootState) => state.user);

  const columns = useMemo(
    () => [
      {
        title: '年份',
        dataIndex: 'year',
      },
      {
        title: '月份',
        dataIndex: 'month',
      },
      {
        title: '問卷標題',
        dataIndex: 'title',
      },
      {
        title: '截止日期',
        dataIndex: 'expireTime',
      },
      {
        title: '狀態',
        dataIndex: 'status',
      },
      {
        title: '填表日期',
        dataIndex: 'submitTime',
      },
      {
        title: '操作',
        cell: (_v: any, _i: any, record: Data) => (
          <Button
            onClick={() => {
              setQuestionnaire({ show: true, currentQuestionnaire: record });
            }}
          >
            開啟問卷
          </Button>
        ),
      },
    ],
    []
  );

  const getTitles = useCallback(async () => {
    try {
      const res = await axios.get<ResponseData>(
        '/ezcon/api/getQuestionnaireTitleFontDesk',
        {
          headers: {
            authorization: localStorage.getItem('feathers-jwt'),
          },
          params: {
            userId: user.auth.user._id,
          },
        }
      );
      let temp: Data[] = [];
      Object.entries(res.data).forEach(([filler, list]) => {
        temp = temp.concat(
          list.map((item: ListItem) => ({
            ...item,
            filler,
          }))
        );
      });
      setDataSource(
        temp
          .sort((a, b) => b.title.localeCompare(a.title))
          .map((item) => ({
            ...item,
            year: item.title.split('-')[0],
            month: item.title.split('-')[1],
          }))
      );
    } catch (err) {
      console.error(err);
    }
  }, [user.auth.user._id]);

  useEffect(() => {
    if (user.auth.user._id) {
      getTitles();
    }
  }, [user.auth.user._id, getTitles, trigger]);

  useEffect(() => {
    const years = new Set(dataSource.map((item) => item.year));
    setYearOptions(
      Array.from(years)
        .sort((a, b) => b.localeCompare(a))
        .map((item) => ({
          label: item,
          value: item,
        }))
    );
    const months = new Set(dataSource.map((item) => item.month));
    setMonthOptions(
      Array.from(months)
        .sort((a, b) => a.localeCompare(b))
        .map((item) => ({
          label: item,
          value: item,
        }))
    );
  }, [dataSource]);

  return (
    <Box>
      {showHeader !== false && (
        <PageHeader
          title="問卷填寫"
          breadcrumbs={['問卷管理', '問卷填寫'].map((i) => ({ name: i }))}
        />
      )}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          gap: '10px',
          mb: '10px',
        }}
      >
        <Select
          placeholder="選擇年份"
          allowClear
          style={{ width: 120 }}
          options={yearOptions}
          value={year}
          onChange={(val) => setYear(val)}
        />
        <Select
          placeholder="選擇月份"
          allowClear
          style={{ width: 120 }}
          options={monthOptions}
          value={month}
          onChange={(val) => setMonth(val)}
        />
        <Select
          allowClear
          placeholder="篩選狀態"
          value={status}
          onChange={(val) => setStatus(val)}
        >
          <Select.Option value="未填寫">未填寫</Select.Option>
          <Select.Option value="已填寫">已填寫</Select.Option>
          <Select.Option value="已截止">已截止</Select.Option>
        </Select>
      </Box>
      <Box sx={{ padding: '20px', borderRadius: '4px', background: '#fff' }}>
        <Table
          maxBodyHeight={100}
          dataSource={dataSource.filter((data) => {
            if (year && month) {
              return data.year === year && data.month === month;
            }
            if (year) {
              return data.year === year;
            }
            if (month) {
              return data.month === month;
            }
            if (status) {
              return data.status === status;
            }
            return true;
          })}
          columns={columns}
        />
      </Box>
      <QuestionnaireForm {...{ questionnaire, setQuestionnaire, setTrigger }} />
    </Box>
  );
};

export default CompanyRating;
